import {
    asArrayValue,
    asBoolean,
    asString,
    loadedDefinedMap,
    loadedDefinedMapWithDefault,
} from "common/components/component-helpers";
import { ActionSource, LOADING, asUI } from "common/components/types";
import { GlideFunctionalRenderer } from "common/components/uis";
import { ImageAspectRatio, ImageFitStyle, ImageGravity, Sizing } from "common/generator/components/image-types";
import { definedMapWithDefault, mapFilterUndefined } from "common/Support";
import { useForce } from "hooks/use-force";
import { useNetworkStatus } from "hooks/use-network-status";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useContext } from "react";

import ImageCarousel from "../ui/smart/image-carousel/image-carousel";
import { HighlightGrowContext } from "./highlight-component-material-renderer";

export const ImageMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [
        [
            image,
            ,
            size,
            sizing,
            onChange, // Refer to this for upload
            isEnabled,
            style,
            enlarge,
            onTap,
            showUploadButton,
            gravity,
            topLeftOverlay,
            topRightOverlay,
            bottomLeftOverlay,
            bottomRightOverlay,
        ],
        [imageVP, validFlag],
    ] = useForceValueProducers(getValueProducers, force, ctx);

    const setGrow = useContext(HighlightGrowContext);
    setGrow("last");

    // FIXME: Use `targetURL`, too
    const { onClick } = useOnClickFromAction(onTap, ActionSource.Image, ctx, env);
    const { onClick: onChangeCB } = useOnClickFromAction(onChange, ActionSource.Image, ctx, env);
    const onLine = useNetworkStatus();

    if (image === LOADING || isEnabled === LOADING) return env.renderProgress();

    const sizingValue = loadedDefinedMapWithDefault(sizing, Sizing.Cover, asString) as Sizing;
    const sizeValue = loadedDefinedMapWithDefault(size, ImageAspectRatio.Square, asString) as ImageAspectRatio;
    const styleValue = loadedDefinedMapWithDefault(style, ImageFitStyle.FullWidth, asString) as ImageFitStyle;
    const enlargeValue = loadedDefinedMapWithDefault(enlarge, false, asBoolean) as boolean;
    const gravityValue = loadedDefinedMapWithDefault(gravity, ImageGravity.Center, asString) as ImageGravity;
    const showUploadButtonValue = loadedDefinedMapWithDefault(showUploadButton, false, asBoolean);
    // onChange can be undefined: it's only around to set columns for non-mutating screens,
    // which don't have a temporary context. But if it's LOADING then we can't do anything yet.
    const uploadDisabled = !onLine || !definedMapWithDefault(isEnabled, true, asBoolean) || onChange === LOADING;

    const onUploadImage = async (imageURL: string) => {
        imageVP?.set(imageURL);
        if (onChangeCB !== undefined) {
            await onChangeCB(undefined);
        }
    };

    if (image === undefined && !showUploadButtonValue) return null;

    const origImages = Array.isArray(image)
        ? mapFilterUndefined(asArrayValue(image), vp => loadedDefinedMap(vp.current, asString))
        : [asString(image)];

    return (
        <ImageCarousel
            appEnvironment={env.appEnvironment}
            fitStyle={styleValue}
            gravity={gravityValue}
            images={origImages}
            size={sizeValue}
            sizing={sizingValue}
            tapToEnlarge={enlargeValue}
            uploadDisabled={uploadDisabled}
            showUploadButton={showUploadButtonValue}
            onClickItem={onClick}
            onUploadStart={uploadDisabled ? undefined : () => validFlag?.set(false)}
            onUploadEnd={
                uploadDisabled
                    ? undefined
                    : async (_index, src) => {
                          validFlag?.set(true);
                          if (src !== undefined) {
                              await onUploadImage(src);
                          }
                      }
            }
            topLeftOverlay={loadedDefinedMap(topLeftOverlay, o => asUI(o).render(ctx, env))}
            bottomLeftOverlay={loadedDefinedMap(bottomLeftOverlay, o => asUI(o).render(ctx, env))}
            bottomRightOverlay={loadedDefinedMap(bottomRightOverlay, o => asUI(o).render(ctx, env))}
            topRightOverlay={loadedDefinedMap(topRightOverlay, o => asUI(o).render(ctx, env))}
        />
    );
};
